import classNames from "classnames"
import React from "react"

import { Analytics } from "@bounce/analytics"
import { useTranslation, useFormatIsoDate, useFormatRelativeDate } from "@bounce/i18n"
import { useReviewUsername } from "@bounce/reviews"
import type { ReviewProps } from "@bounce/web-components"
import { Review } from "@bounce/web-components"

import { InteractionEvents } from "../../../../libs/analytics/events"
import type { Review as TReview } from "../../../../utils/reviews"
import SafeLink from "../../SafeLink/SafeLink"

export type ReviewCardProps = {
  review: TReview
  className?: string
  avatarClassName?: string
  numberOfLines?: ReviewProps["numberOfLines"]
}

const ReviewCardMetadata = ({ review }: Pick<ReviewCardProps, "review">) => {
  const formatRelative = useFormatRelativeDate()
  const formatIsoDate = useFormatIsoDate()

  return (
    <div className="flex flex-col">
      <time dateTime={formatIsoDate(review.completedAt)} suppressHydrationWarning>
        {formatRelative(review.completedAt)}
      </time>
    </div>
  )
}

export const ReviewCard = ({ review, className, avatarClassName = "", numberOfLines = 4 }: ReviewCardProps) => {
  const { t } = useTranslation()
  const name = useReviewUsername({ review })

  return (
    <Review
      className={classNames("rounded-lg border-2 border-neutral-300 px-5 py-4 xl:px-8 xl:py-6", className)}
      feedback={review.translatedFeedback ?? review.feedback}
      name={name}
      numberOfLines={numberOfLines}
      rating={review.rating}
      externalSource={review.externalSource}
      externalSourceAttribution={
        review.externalSource ? (
          <>
            {t("cmp.reviewCardNext.externalSource", "From:")}{" "}
            <SafeLink href={review.externalSource} className="review__external-source underline">
              Google
            </SafeLink>
          </>
        ) : undefined
      }
      avatarSrc={review.user.image ?? undefined}
      avatarClassName={avatarClassName}
      avatarAlt={t(
        "cmp.reviewCardNext.photoAltText",
        "Photo of {name}, a customer who reviewed Bounce in {location}.",
        {
          name,
          location: review.city.translatedName,
        },
      )}
      metaData={<ReviewCardMetadata review={review} />}
      onClickTruncate={(isOpen: boolean) =>
        Analytics.track(InteractionEvents.TruncateToggled, { name: "ReviewCard", open: isOpen })
      }
    />
  )
}
